import { t } from "ttag";

import type { MetricFilterControlsProps } from "metabase/browse/metrics/types";
import type { ModelFilterControlsProps } from "metabase/browse/models/types";
import type { SearchFilterToggle } from "metabase/common/search/types";
import type { State } from "metabase/redux/store";
import { getSetting, useUserSetting } from "metabase/settings";
import { Switch } from "metabase/ui";

type VerifiedOnlySwitchProps = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

function VerifiedOnlySwitch({
  label,
  checked,
  onChange,
}: VerifiedOnlySwitchProps) {
  return (
    <Switch
      label={label}
      labelPosition="left"
      checked={checked}
      onChange={(event) => onChange(event.currentTarget.checked)}
    />
  );
}

/** The "Verified models only" switch on Browse > Models, remembered per user. */
export function ModelFilterControls({
  modelFilters,
  setModelFilters,
}: ModelFilterControlsProps) {
  const [, setOnlyVerifiedModels] = useUserSetting(
    "browse-filter-only-verified-models",
  );

  const handleChange = (verified: boolean) => {
    setModelFilters({ ...modelFilters, verified });
    setOnlyVerifiedModels(verified);
  };

  return (
    <VerifiedOnlySwitch
      label={t`Verified models only`}
      checked={modelFilters.verified ?? false}
      onChange={handleChange}
    />
  );
}

/** The "Verified metrics only" switch on Browse > Metrics, remembered per user. */
export function MetricFilterControls({
  metricFilters,
  setMetricFilters,
}: MetricFilterControlsProps) {
  const [, setOnlyVerifiedMetrics] = useUserSetting(
    "browse-filter-only-verified-metrics",
  );

  const handleChange = (verified: boolean) => {
    setMetricFilters({ ...metricFilters, verified });
    setOnlyVerifiedMetrics(verified);
  };

  return (
    <VerifiedOnlySwitch
      label={t`Verified metrics only`}
      checked={metricFilters.verified ?? false}
      onChange={handleChange}
    />
  );
}

export const getDefaultModelFilters = (state: State) => ({
  verified: getSetting(state, "browse-filter-only-verified-models") ?? true,
});

export const getDefaultMetricFilters = (state: State) => ({
  verified: getSetting(state, "browse-filter-only-verified-metrics") ?? true,
});

export const VerifiedFilter: SearchFilterToggle = {
  label: () => t`Verified items only`,
  iconName: "verified",
  type: "toggle",
  fromUrl: (value) => value === "true",
  toUrl: (value: boolean) => (value ? "true" : null),
};
